import { useEffect, useState } from 'react'
import { useAuth } from 'react-oidc-context'
import TameeniLoaderOverlay from '@/components/tameeni-loader/tameeni-loader-overlay'
import { usePostLogin } from '@/features/authentication'

type AuthPreloadRoutesProps = {
  children: React.ReactNode
}

/**
 * Gates the first paint on auth initialisation.
 *
 * Token renewal used to live here too, but this component only mounts when the
 * app runs standalone — renewal must not depend on that — so it now belongs to
 * SessionRenewalBridge.
 *
 * The removed on-load effect also attempted a silent sign-in when there was no
 * local session at all. That could never succeed in this configuration: with no
 * stored user there is no refresh token, so signinSilent falls through to its
 * iframe path and throws `No silent_redirect_uri configured` (none is set). The
 * case that does work — a stored user whose token expired — is handled by the
 * bridge's startup recovery.
 */
const AuthPreloadRoutes = ({ children }: AuthPreloadRoutesProps) => {
  const { isLoading } = useAuth()
  /**
   * Only show the full-screen loader on the very first auth initialisation.
   * Subsequent `isLoading` flips (e.g. from silent renew or from the user
   * clicking login) must NOT unmount the tree — otherwise the parallel
   * `@modal` slot (LoginModal + iframe) tears down and remounts, which
   * appears to the user as the popup opening, refreshing, closing and
   * opening several times.
   */
  const [hasCompletedInitialLoad, setHasCompletedInitialLoad] = useState(false)

  useEffect(() => {
    if (!isLoading && !hasCompletedInitialLoad) {
      setHasCompletedInitialLoad(true)
    }
  }, [isLoading, hasCompletedInitialLoad])

  usePostLogin()

  if (isLoading && !hasCompletedInitialLoad) {
    return <TameeniLoaderOverlay transparent={false} />
  }

  return <>{children}</>
}

export default AuthPreloadRoutes
